import { Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { totalAppointments } from '#lib/react-query/queries.ts';
import { STAT_WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from '../Base.tsx';
import {
  InfoCalculation,
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '../InfoTemplateComponents.tsx';
import { useAppointmentWidget } from '../use-appointment-widget.ts';
import { StatBase } from './StatBase.tsx';

const widgetTitle = 'Total Appointments';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Shows the total number of appointments scheduled to start within the selected date range.
        This helps you quickly measure appointment volume and track scheduling trends.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoCalculation
        formula="Count of distinct:"
        steps={[
          'Appointments that are not canceled or deleted',
          'Appointments that started within the selected date range',
          'Appointments that match any filters you’ve applied (e.g., location, provider)',
        ]}
      />

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Appointments with valid statuses (anything except canceled or deleted)',
            'Appointments that are not marked as no-shows',
            'Appointments starting within the selected date range',
            'Appointments matching all additional filters (e.g., date, location, provider)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList
          items={[
            'Canceled appointments',
            'Deleted appointments',
            'Appointments outside the selected date range or excluded by other filters',
          ]}
        />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            <>
              Metric is based on the&nbsp;
              <InfoEmphasis>scheduled start time</InfoEmphasis>, not creation or completion time
            </>,
            '“Distinct appointment IDs” ensures the same appointment is not counted more than once',
            'Drilldown is available to see the underlying appointment list',
            'Mutually exclusive logic: A canceled, or deleted, appointment will never be counted, even if it meets the date criteria',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

export function TotalAppointments() {
  const widget = useAppointmentWidget();

  const { data, isLoading } = useQuery(totalAppointments(widget.getCommonRequestParams()));

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={STAT_WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <StatBase
      data={{
        id: 'total-appointments',
        title: widgetTitle,
        value: data?.value ?? null,
        valueType: 'absolute',
        trend: data?.trend
          ? {
              ...data.trend,
              daysInPeriod: widget.homeLoaderData.totalDaysInRange,
            }
          : undefined,
      }}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    />
  );
}
